import * as React from "react";
import { useEffect } from "react";
import "./style.css";
// MUI components
import { Paper, Typography, Button, Grid, Box } from "@mui/material";

// API client for backend requests
import apiClient from "../../../utils/api";

// datam process components
import { renderPlot } from "./utils";
import { BriefTable } from "./table";
import { DateFilter } from "./Filter";
import PlotSwitcher from "./Switch";
import {
  Interaction,
  SessionActivity,
  pdfAction,
  chatAction,
  codeAction
} from "./interface";

function transformInteractionsToSessionActivities(
  interactions: Interaction[],
  pdfIdNamePairs: { [key: string]: string } = {}
): SessionActivity[] {
  const sessionMap: { [key: string]: SessionActivity } = {};

  interactions.forEach((interaction) => {
    const sessionKey = `${interaction.userId}-${interaction.sessionId}`;
    const interactionTimestamp = new Date(interaction.timestamp);

    // Validate the interaction timestamp
    // if (isNaN(interactionTimestamp.getTime())) {
    //     console.error(`Invalid timestamp: ${interaction.timestamp}`);
    //     return;
    // }

    if (!sessionMap[sessionKey]) {
      sessionMap[sessionKey] = {
        userId: interaction.userId,
        sessionId: interaction.sessionId,
        sessionStartTime: interactionTimestamp,
        totalWatchTime: 0,
        pdfActions: [],
        codeActions:[],
        chatActions: [],
        otherActions: [],
        weekStart: interactionTimestamp,
      };
    }

    interaction.actions.forEach((action) => {
      const actionTimestamp = new Date(parseInt(action.timestamp, 10));

      // Validate each action's timestamp
      if (isNaN(actionTimestamp.getTime())) {
        console.error(`Invalid timestamp: ${action.timestamp}`);
        return;
      }

      // Process based on the action name
      // if starts with chat
      if (action.name.startsWith("chat")) {
        sessionMap[sessionKey].chatActions.push(action as chatAction);
      } else if (action.name === "frontend-interaction-pdf-change") {
        sessionMap[sessionKey].pdfActions.push(action as pdfAction);
      } else if (action.name.startsWith("frontend-interaction-ide")) {
        sessionMap[sessionKey].codeActions.push(action as codeAction);
      } else {
        sessionMap[sessionKey].otherActions.push(action);
      }
    });

    // Update sessionStartTime if the current interaction's timestamp is earlier
    const sessionStartTime = sessionMap[sessionKey].sessionStartTime;
    if (interactionTimestamp < sessionStartTime) {
      sessionMap[sessionKey].sessionStartTime = interactionTimestamp;
    }
  });
  Object.keys(sessionMap).forEach((sessionKey) => {
    const session = sessionMap[sessionKey];

    // convert pdfActions pdfid to pdfname
    session.pdfActions = session.pdfActions.map((action) => {
      action.context.pdfID = pdfIdNamePairs[action.context.pdfID];
      return action;
    });

    // Merge all types of actions into one array and sort by timestamp
    const allActions = [
      ...session.pdfActions,
      ...session.otherActions,
      ...session.chatActions,
    ];

    allActions.sort((a, b) => {
      // Parse timestamps to compare correctly
      const timestampA = parseTimestamp(a.timestamp);
      const timestampB = parseTimestamp(b.timestamp);
      return timestampA - timestampB;
    });

    // Function to parse timestamps into comparable values (milliseconds)
    function parseTimestamp(timestamp: string): number {
      if (typeof timestamp === "string") {
        // Check if the timestamp is in ISO 8601 format
        if (timestamp.match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/)) {
          return new Date(timestamp).getTime();
        } else {
          // Assume it's a Unix timestamp in milliseconds
          return new Date(Number(timestamp)).getTime();
        }
      }
      // If it's already a number (Unix timestamp in milliseconds or seconds)
      return timestamp;
    }

    // Calculate total watch time based on the first and last action's timestamps
    if (allActions.length > 0) {
      const first = allActions[0];
      const last = allActions[allActions.length - 1];
      const firstActionTime = new Date(parseTimestamp(first.timestamp));
      const lastActionTime = new Date(parseTimestamp(last.timestamp));
      console.log("First Action Time", new Date(firstActionTime));
      console.log("Last Action Time", new Date(lastActionTime));
      // const totalWatchTimeMs = lastActionTime - firstActionTime;

      const totalWatchTimeMs =
        lastActionTime.getTime() - firstActionTime.getTime();
      // console.log("Difference in Milliseconds", differenceInMillis);
      // Convert milliseconds to seconds (divide by 1000)

      const totalWatchTime = totalWatchTimeMs / (60 * 60 * 1000); // Convert milliseconds to hours
      console.log("Total Watch Time", totalWatchTime);
      sessionMap[sessionKey].totalWatchTime = totalWatchTime;
    } else {
      session.totalWatchTime = 0;
    }

    // Separate actions back into their respective arrays
    session.pdfActions = allActions.filter(
      (action) => action.name === "frontend-interaction-pdf-change"
    ) as pdfAction[];
    session.otherActions = allActions.filter(
      (action) =>
        action.name !== "frontend-interaction-pdf-change" && action.name !== ""
    );
    session.chatActions = allActions.filter((action) =>
      [
        "frontend-interaction-chat"
      ].includes(action.name)
    ) as chatAction[];
  });

  return Object.values(sessionMap);
}

export const Analytics: React.FC = () => {
  //   const [interactions, setInteractions] = React.useState<Interaction[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [dateFilter, setDateFilter] = React.useState<{
    start: Date;
    end: Date;
  }>({
    start: new Date(),
    end: new Date(),
  });
  //   const [datefilted, setDateFiltered] = React.useState<boolean>(false);
  // with filters, the filtered data will be passed to the plot components

  const [selectedPlot, setSelectedPlot] = React.useState<string>("SessionPlot");
  const [sessionActivities, setSessionActivities] = React.useState<
    SessionActivity[]
  >([]);
  const [pdfIdNamePairs, setPdfIdNamePairs] = React.useState<{
    [key: string]: string;
  }>({});
  // const [filteredsessionActivities, setFilteredsessionActivities] = React.useState<SessionActivity[]>([]);
  const [page, setPage] = React.useState(1);

  useEffect(() => {
    let endDate = new Date().toISOString();

    // Calculate startDate (one week ago from today)
    let startDate = new Date(
      new Date().getTime() - 7 * 24 * 60 * 60 * 1000
    ).toISOString();

    let pairs = {};
    apiClient.classControllerList().then((res) => {
      const classes = res.classIds;
      classes.forEach((element) => {
        apiClient
          .classControllerGetDocumentListIds({
            classID: element,
          })
          .then((res) => {
            const pdfs = res.documentIds;
            pdfs.forEach((pdf) => {
              const pdfId = pdf.id;
              const pdfFileName = pdf.fileName;
              pairs = { ...pairs, [pdfId]: pdfFileName };
              setPdfIdNamePairs({ ...pairs, [pdfId]: pdfFileName });
            });
          });
      });
    });

    apiClient
      .analyticsControllerPresentDateFilteredInteractions({
        dateFilterRequest: {
          startDate: startDate,
          endDate: endDate,
        },
      })
      .then((got) => {
        console.log(got);
        const gotInteractions = got as unknown as Interaction[];

        console.log("Got interactions", gotInteractions);
        // setInteractions(gotInteractions);

        const sessionsActions =
          transformInteractionsToSessionActivities(gotInteractions);
        console.log("Session Actions", sessionsActions);
        setSessionActivities(sessionsActions);
      });
  }, []);
  //  set up when get into the page
  useEffect(() => {
    // setFilteredsessionActivities(filterDataForWeek(sessionActivities, dateFilter.start, dateFilter.end));
    // setDateFiltered(true);
    apiClient
      .analyticsControllerPresentDateFilteredInteractions({
        dateFilterRequest: {
          startDate: dateFilter.start.toISOString(),
          endDate: dateFilter.end.toISOString(),
        },
      })
      .then(async (got) => {
        const gotInteractions = got as unknown as Interaction[];

        console.log("Got interactions", gotInteractions);
        // setInteractions(gotInteractions);

        const sessionsActions = transformInteractionsToSessionActivities(
          gotInteractions,
          pdfIdNamePairs
        );
        console.log("Session Actions", sessionsActions);
        setSessionActivities(sessionsActions);
      });
  }, [dateFilter, pdfIdNamePairs]);

  const handleRefresh = () => {
    setLoading(true);

    apiClient
      .analyticsControllerPresentDateFilteredInteractions({
        dateFilterRequest: {
          startDate: dateFilter.start.toISOString(),
          endDate: dateFilter.end.toISOString(),
        },
      })
      .then(async (got) => {
        const gotInteractions = got as unknown as Interaction[];
        console.log("Got interactions", gotInteractions);
        const sessionsActions = transformInteractionsToSessionActivities(
          gotInteractions,
          pdfIdNamePairs
        );
        console.log("Session Actions", sessionsActions);
        setSessionActivities(sessionsActions);
      });
    setLoading(false);
  };

  return (
    <div id="container" style={{ padding: "20px" }}>
      <div>
        <h1>Interaction Analytics</h1>
      </div>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          {/* Placeholder for Plot 1 */}
          <Paper
            style={{
              height: "300px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {renderPlot(selectedPlot, sessionActivities)}
          </Paper>
        </Grid>
        <Grid item xs={6}>
          {/* Placeholder for Plot 2 */}
          <Paper
            style={{
              height: "300px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* All of the cumulative ones */}

            {renderPlot(selectedPlot, sessionActivities)}
          </Paper>
        </Grid>
        <Grid item xs={12}>
          {/* Control Panel */}
          <Box
            display="flex"
            justifyContent="Left"
            alignItems="center"
            marginBottom="20px"
          >
            <Paper
              style={{
                width: "350px",
                height: "50px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginRight: "20px",
              }}
            >
              <Typography variant="h6" gutterBottom>
                Filtered Session Number: {sessionActivities.length}
                {}
              </Typography>
            </Paper>
            <Button variant="contained" onClick={handleRefresh}>
              Refresh
            </Button>

            <PlotSwitcher switching={setSelectedPlot} />
            <DateFilter
              interactions={sessionActivities}
              setDateFilter={setDateFilter}
            />
            <Typography>Current Table Page: {page}</Typography>
          </Box>
        </Grid>

        <BriefTable
          sessionActivities={sessionActivities}
          pdfIdNamePairs={pdfIdNamePairs}
          loading={loading}
          setPageTrans={setPage}
        />
      </Grid>
    </div>
  );
};
